import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { getCookies } from '../../lib/getCookies';
import { getGAClientId } from '../../lib/getGAClientId';
import { getGATrackingId } from '../../lib/getGATrackingId';
import { useCountry } from '../../components/CountrySelector';
import { useSegmentAnonymousId } from '../useSegmentAnonymousId';
import { useCart } from '../useCart';
import { useUser } from '../useUser';
import { useSignifydSessionId } from '../useSignifydSessionId';
import { GET_REWARDS_SUMMARY_QUERY } from '../useRewardsSummary';
import {
  PLACE_ORDER_MUTATION,
  PlaceOrderMutation,
  PlaceOrderMutationVariables
} from './placeOrderMutation.rest';

export const usePlaceOrderMutation = () => {
  const segmentAnonymousId = useSegmentAnonymousId();
  const { country } = useCountry();
  const { signifydSessionId, clearSignifydSessionId } = useSignifydSessionId();
  const {
    cart: {
      id: cartId,
      attributes: {
        addressId,
        giftCode,
        paymentMethodId,
        selectedShippingMethodId,
        paymentSource,
        error: cartError
      }
    }
  } = useCart();
  const hasInvalidCoupon = cartError?.type.name === 'Invalid Coupon';
  const {
    user: { isLoggedIn }
  } = useUser();

  const [mutate, { loading, error }] = useMutation<PlaceOrderMutation, PlaceOrderMutationVariables>(
    PLACE_ORDER_MUTATION,
    {
      refetchQueries: isLoggedIn
        ? [
            {
              query: GET_REWARDS_SUMMARY_QUERY,
              variables: {
                countryCode: country.alpha2Code,
                currency: country.currencyCode
              }
            }
          ]
        : []
    }
  );

  const handlePlaceOrder = useCallback(
    async (customCartAttributes = null) => {
      if (!addressId && !customCartAttributes)
        return Promise.reject('placeOrderMutation: no address selected');
      if (!cartId && !customCartAttributes)
        return Promise.reject('placeOrderMutation: cart id not found');
      if (!paymentMethodId && !customCartAttributes)
        return Promise.reject('placeOrderMutation: no payment method selected');
      if (!selectedShippingMethodId && !customCartAttributes)
        return Promise.reject('placeOrderMutation: no shipping method selected');
      if (!paymentSource && !customCartAttributes)
        return Promise.reject('placeOrderMutation: payment source not found');

      const cookies = getCookies();

      const result = await mutate({
        variables: {
          input: {
            data: {
              type: 'orders',
              attributes: customCartAttributes
                ? {
                    ...customCartAttributes,
                    order_session_id: signifydSessionId,
                    event_source: 'astrarium'
                  }
                : {
                    address_id: addressId,
                    cart_id: cartId,
                    country_code: country.alpha2Code,
                    event_source: 'astrarium',
                    offer_code: !hasInvalidCoupon ? giftCode : null,
                    order_session_id: signifydSessionId,
                    payment_id: paymentMethodId,
                    payment_source: paymentSource,
                    shipment_method: selectedShippingMethodId,
                    linkshare: cookies.linkshare,
                    ga_id: getGAClientId(),
                    ga_tracking_id: getGATrackingId(),
                    segment_id: segmentAnonymousId
                  }
            }
          }
        }
      });

      clearSignifydSessionId();

      return result;
    },
    [
      cartId,
      addressId,
      giftCode,
      paymentMethodId,
      selectedShippingMethodId,
      paymentSource,
      country,
      mutate,
      signifydSessionId,
      clearSignifydSessionId,
      segmentAnonymousId,
      hasInvalidCoupon
    ]
  );

  return [handlePlaceOrder, { loading, error }] as const;
};
